import { Post } from '../types';
import { openaiService, ContentGenerationRequest, ContentGenerationResponse, WritingSuggestion, SubjectLineOptions } from './openaiService';

// Re-export types for convenience
export type { WritingSuggestion, SubjectLineOptions } from './openaiService';

export interface AIContentRequest {
  type: 'newsletter' | 'article' | 'announcement' | 'poll' | 'event' | 'media' | 'series';
  topic?: string;
  tone?: 'professional' | 'casual' | 'friendly' | 'authoritative';
  length?: 'short' | 'medium' | 'long';
  audience?: string;
  keywords?: string[];
  template?: string;
}

export interface AIContentResponse {
  title: string;
  content: string;
  excerpt: string;
  tags: string[];
  suggestions: string[];
  confidence: number;
}

class AIContentService {
  constructor() {
    console.log('AIContentService initialized with OpenAI integration');
  }

  async generateContent(request: AIContentRequest): Promise<AIContentResponse> {
    try {
      // Use real OpenAI service
      const openaiRequest: ContentGenerationRequest = {
        type: request.type,
        topic: request.topic,
        tone: request.tone,
        length: request.length,
        audience: request.audience,
        keywords: request.keywords,
        template: request.template
      };

      const response = await openaiService.generateContent(openaiRequest);
      
      return {
        title: response.title,
        content: response.content,
        excerpt: response.excerpt,
        tags: response.tags,
        suggestions: response.suggestions,
        confidence: response.confidence
      };
    } catch (error) {
      console.error('Error generating AI content:', error);
      // Fallback to mock content if OpenAI fails
      return this.generateMockContent(request);
    }
  }

  async improveContent(content: string, improvements: string[]): Promise<string> {
    try {
      // Use real OpenAI service for content improvement
      return await openaiService.improveContent(content, improvements);
    } catch (error) {
      console.error('Error improving content:', error);
      // Fallback to mock improvement
      return this.mockImproveContent(content, improvements);
    }
  }

  async transcribeAudio(audioBlob: Blob): Promise<string> {
    try {
      // Use real OpenAI service for audio transcription
      return await openaiService.transcribeAudio(audioBlob);
    } catch (error) {
      console.error('Error transcribing audio:', error);
      // Fallback to mock transcription
      return this.mockTranscribeAudio();
    }
  }

  async generateSuggestions(context: string): Promise<string[]> {
    try {
      // Use real OpenAI service for writing suggestions
      const suggestions = await openaiService.generateWritingSuggestions(context);
      return suggestions.map(s => s.suggestion);
    } catch (error) {
      console.error('Error generating suggestions:', error);
      // Fallback to mock suggestions
      return this.mockGenerateSuggestions(context);
    }
  }

  // New method for detailed writing suggestions
  async getWritingSuggestions(content: string): Promise<WritingSuggestion[]> {
    try {
      return await openaiService.generateWritingSuggestions(content);
    } catch (error) {
      console.error('Error getting writing suggestions:', error);
      return [];
    }
  }

  // New method for subject line generation
  async generateSubjectLines(content: string, count: number = 5): Promise<SubjectLineOptions> {
    try {
      return await openaiService.generateSubjectLines(content, count);
    } catch (error) {
      console.error('Error generating subject lines:', error);
      return {
        options: ['Sample Subject Line'],
        analysis: {
          best: 'Sample Subject Line',
          reasoning: 'Fallback option',
          openRatePrediction: 0.5
        }
      };
    }
  }

  private generateMockContent(request: AIContentRequest): AIContentResponse {
    const { type, topic, tone = 'professional', length = 'medium', audience, keywords = [] } = request;
    
    const templates = {
      newsletter: {
        title: `Weekly Update: ${topic || 'Community News'}`,
        content: this.generateNewsletterContent(topic, tone, length),
        excerpt: `Weekly community update covering ${topic || 'key developments'}`,
        tags: ['newsletter', 'weekly', 'update', ...keywords],
        suggestions: [
          'Add subscriber testimonials',
          'Include upcoming events',
          'Add community highlights',
          'Include call-to-action'
        ]
      },
      article: {
        title: `The Complete Guide to ${topic || 'Your Topic'}`,
        content: this.generateArticleContent(topic, tone, length),
        excerpt: `Comprehensive guide covering ${topic || 'essential information'}`,
        tags: ['article', 'guide', 'tutorial', ...keywords],
        suggestions: [
          'Add more examples',
          'Include statistics',
          'Add personal anecdotes',
          'Include actionable steps'
        ]
      },
      announcement: {
        title: `Important Announcement: ${topic || 'Update'}`,
        content: this.generateAnnouncementContent(topic, tone),
        excerpt: `Important announcement regarding ${topic || 'upcoming changes'}`,
        tags: ['announcement', 'urgent', 'news', ...keywords],
        suggestions: [
          'Add timeline details',
          'Include contact information',
          'Add FAQ section',
          'Include next steps'
        ]
      },
      poll: {
        title: `Poll: What do you think about ${topic || 'this topic'}?`,
        content: this.generatePollContent(topic, tone),
        excerpt: `Interactive poll asking for your opinion on ${topic || 'current topics'}`,
        tags: ['poll', 'survey', 'engagement', ...keywords],
        suggestions: [
          'Add more poll options',
          'Include context',
          'Add deadline',
          'Include results sharing'
        ]
      },
      event: {
        title: `Event: ${topic || 'Community Meetup'}`,
        content: this.generateEventContent(topic, tone),
        excerpt: `Join us for ${topic || 'our upcoming event'}`,
        tags: ['event', 'community', 'meetup', ...keywords],
        suggestions: [
          'Add RSVP link',
          'Include agenda',
          'Add speaker bios',
          'Include location details'
        ]
      },
      media: {
        title: `Media Content: ${topic || 'Visual Story'}`,
        content: this.generateMediaContent(topic, tone),
        excerpt: `Engaging media content about ${topic || 'visual storytelling'}`,
        tags: ['media', 'video', 'audio', ...keywords],
        suggestions: [
          'Add video transcript',
          'Include audio description',
          'Add captions',
          'Include download links'
        ]
      },
      series: {
        title: `Series: ${topic || 'Learning Journey'} - Part 1`,
        content: this.generateSeriesContent(topic, tone, length),
        excerpt: `First part of our series on ${topic || 'comprehensive learning'}`,
        tags: ['series', 'tutorial', 'multi-part', ...keywords],
        suggestions: [
          'Add series overview',
          'Include part navigation',
          'Add progress tracking',
          'Include series completion'
        ]
      }
    };

    const template = templates[type];
    return {
      ...template,
      confidence: 0.85
    };
  }

  private generateNewsletterContent(topic: string | undefined, tone: string, length: string): string {
    const greeting = tone === 'casual' ? 'Hey there!' : 'Hello everyone!';
    const signoff = tone === 'casual' ? 'Thanks for reading!' : 'Thank you for being part of our community!';
    
    return `
      <h2>${greeting}</h2>
      <p>Welcome to this week's newsletter! Here's what we're covering:</p>
      
      <h3>This Week's Highlights</h3>
      <ul>
        <li>Key updates and announcements</li>
        <li>Featured content and resources</li>
        <li>Community highlights and achievements</li>
        <li>Upcoming events and opportunities</li>
      </ul>
      
      <h3>Featured Content</h3>
      <p>${topic ? `This week we're focusing on ${topic}.` : 'We have some exciting content to share with you.'}</p>
      
      <h3>Community Spotlight</h3>
      <p>Shoutout to our amazing community members who are making a difference!</p>
      
      <h3>What's Next</h3>
      <p>Looking ahead to next week, we have some exciting plans...</p>
      
      <p>${signoff}</p>
    `;
  }

  private generateArticleContent(topic: string | undefined, tone: string, length: string): string {
    return `
      <h1>The Complete Guide to ${topic || 'Your Topic'}</h1>
      <p>In this comprehensive guide, we'll explore everything you need to know about ${topic || 'this important topic'}.</p>
      
      <h2>Introduction</h2>
      <p>Understanding ${topic || 'this concept'} is crucial for success. Let's dive deep into the fundamentals.</p>
      
      <h2>Key Concepts</h2>
      <ul>
        <li>Fundamental principles</li>
        <li>Best practices</li>
        <li>Common pitfalls to avoid</li>
        <li>Advanced techniques</li>
      </ul>
      
      <h2>Step-by-Step Process</h2>
      <ol>
        <li><strong>Step 1:</strong> Initial setup and preparation</li>
        <li><strong>Step 2:</strong> Implementation and execution</li>
        <li><strong>Step 3:</strong> Testing and validation</li>
        <li><strong>Step 4:</strong> Optimization and improvement</li>
      </ol>
      
      <h2>Real-World Examples</h2>
      <p>Here are some practical examples to illustrate the concepts:</p>
      
      <h2>Conclusion</h2>
      <p>By following this guide, you'll be well-equipped to ${topic ? `master ${topic}` : 'achieve your goals'}.</p>
    `;
  }

  private generateAnnouncementContent(topic: string | undefined, tone: string): string {
    return `
      <div className="announcement-container">
        <h2 className="announcement-title">📢 Important Announcement</h2>
        <p className="announcement-text">${topic || 'We have an important update to share with our community.'}</p>
      </div>
      
      <h3>What's Changing</h3>
      <p>We're excited to announce some important updates that will enhance your experience.</p>
      
      <h3>Timeline</h3>
      <ul>
        <li><strong>Effective Date:</strong> [Date]</li>
        <li><strong>Implementation:</strong> [Timeline]</li>
        <li><strong>Support:</strong> Available throughout transition</li>
      </ul>
      
      <h3>What This Means for You</h3>
      <p>These changes will bring you:</p>
      <ul>
        <li>Improved functionality</li>
        <li>Better user experience</li>
        <li>Enhanced features</li>
        <li>Continued support</li>
      </ul>
      
      <h3>Next Steps</h3>
      <p>No action required on your part. We'll keep you updated throughout the process.</p>
    `;
  }

  private generatePollContent(topic: string | undefined, tone: string): string {
    return `
      <h2>What do you think about ${topic || 'this topic'}?</h2>
      <p>We'd love to hear your opinion and get your input on this important question.</p>
      
      <div className="info-container">
        <h3>Poll Question</h3>
        <p><strong>${topic || 'What is your preferred approach?'}</strong></p>
        
        <div style="margin: 16px 0;">
          <label style="display: block; margin: 8px 0;">
            <input type="radio" name="poll" value="option1" style="margin-right: 8px;">
            Option 1
          </label>
          <label style="display: block; margin: 8px 0;">
            <input type="radio" name="poll" value="option2" style="margin-right: 8px;">
            Option 2
          </label>
          <label style="display: block; margin: 8px 0;">
            <input type="radio" name="poll" value="option3" style="margin-right: 8px;">
            Option 3
          </label>
        </div>
        
        <button style="background: #3b82f6; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
          Submit Vote
        </button>
      </div>
      
      <p>Vote and let us know your thoughts in the comments below!</p>
    `;
  }

  private generateEventContent(topic: string | undefined, tone: string): string {
    return `
      <div className="event-container">
        <h2 className="event-title">🎉 ${topic || 'Community Event'}</h2>
        <div className="event-grid">
          <div><strong>📅 Date:</strong> [Event Date]</div>
          <div><strong>⏰ Time:</strong> [Event Time]</div>
          <div><strong>📍 Location:</strong> [Event Location]</div>
          <div><strong>🎫 RSVP:</strong> [RSVP Link]</div>
        </div>
      </div>
      
      <h3>About This Event</h3>
      <p>Join us for an exciting ${topic || 'community gathering'} where we'll explore new ideas and connect with like-minded individuals.</p>
      
      <h3>What to Expect</h3>
      <ul>
        <li>Keynote presentations</li>
        <li>Interactive workshops</li>
        <li>Networking opportunities</li>
        <li>Q&A sessions</li>
      </ul>
      
      <h3>Who Should Attend</h3>
      <p>This event is perfect for anyone interested in ${topic || 'learning and growing'}.</p>
      
      <h3>Registration</h3>
      <p>Spots are limited, so be sure to RSVP early!</p>
    `;
  }

  private generateMediaContent(topic: string | undefined, tone: string): string {
    return `
      <h2>Media Content: ${topic || 'Visual Story'}</h2>
      <p>Create engaging media content with audio, video, or visuals.</p>
      
      <div className="news-container">
        <h3>🎥 Video Content</h3>
        <p>Upload your video or embed from YouTube/Vimeo</p>
        <div style="background: #e0f2fe; padding: 16px; border-radius: 4px; text-align: center; color: #0369a1;">
          [Video Player Placeholder]
        </div>
      </div>
      
      <div className="success-container">
        <h3>🎵 Audio Content</h3>
        <p>Record audio or upload audio files</p>
        <div style="background: #dcfce7; padding: 16px; border-radius: 4px; text-align: center; color: #166534;">
          [Audio Player Placeholder]
        </div>
      </div>
      
      <h3>Description</h3>
      <p>${topic ? `This media content explores ${topic} through visual and audio storytelling.` : 'Add a description of your media content here...'}</p>
      
      <h3>Transcript</h3>
      <p>For accessibility, here's a transcript of the audio/video content:</p>
      <div style="background: #f9fafb; padding: 16px; border-radius: 4px; font-style: italic;">
        [Transcript will appear here]
      </div>
    `;
  }

  private generateSeriesContent(topic: string | undefined, tone: string, length: string): string {
    return `
      <h1>Series: ${topic || 'Learning Journey'} - Part 1</h1>
      <p>Welcome to the first part of our comprehensive series on ${topic || 'this important topic'}.</p>
      
      <h2>Series Overview</h2>
      <p>In this multi-part series, we'll cover:</p>
      <ul>
        <li><strong>Part 1:</strong> Introduction and fundamentals (this post)</li>
        <li><strong>Part 2:</strong> Intermediate concepts and techniques</li>
        <li><strong>Part 3:</strong> Advanced strategies and implementation</li>
        <li><strong>Part 4:</strong> Best practices and optimization</li>
      </ul>
      
      <h2>What You'll Learn</h2>
      <p>By the end of this series, you'll have a complete understanding of ${topic || 'the subject matter'}.</p>
      
      <h2>Prerequisites</h2>
      <p>No prior experience required. We'll start from the basics and build up.</p>
      
      <h2>Getting Started</h2>
      <p>Let's begin with the fundamentals and set the foundation for the rest of the series.</p>
      
      <h2>Series Navigation</h2>
      <p>Use the navigation below to move between parts of the series:</p>
      <div style="background: #f3f4f6; padding: 16px; border-radius: 8px;">
        <p><strong>Previous:</strong> [Not available] | <strong>Next:</strong> Part 2 - Intermediate Concepts</p>
      </div>
    `;
  }

  private mockImproveContent(content: string, improvements: string[]): string {
    // Mock content improvement - in a real implementation, this would use AI
    return `
      <div style="background: #f0f9ff; border: 1px solid #0ea5e9; border-radius: 8px; padding: 16px; margin: 16px 0;">
        <h3>AI-Improved Content</h3>
        <p>Your content has been enhanced with the following improvements:</p>
        <ul>
          ${improvements.map(improvement => `<li>${improvement}</li>`).join('')}
        </ul>
      </div>
      ${content}
    `;
  }

  private mockTranscribeAudio(): string {
    // Mock audio transcription
    return `
      <div style="background: #f3f4f6; border-radius: 8px; padding: 16px; margin: 16px 0;">
        <h3>Transcribed Content</h3>
        <p><em>This is a mock transcription of your audio content. In a real implementation, this would be the actual transcribed text from your audio recording.</em></p>
        <p>You can edit this content as needed to match your intended message and style.</p>
      </div>
    `;
  }

  private mockGenerateSuggestions(context: string): string[] {
    // Mock suggestion generation
    return [
      'Add more specific examples',
      'Include relevant statistics',
      'Add a call-to-action',
      'Include personal anecdotes',
      'Add visual elements',
      'Include external links',
      'Add a conclusion section',
      'Include related topics'
    ];
  }
}

export const aiContentService = new AIContentService();
